import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Heart, X, ShoppingBag, Lock, Sparkles } from "lucide-react";

export default function TruthMarkers() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [page, setPage] = useState(null);
  const [locations, setLocations] = useState([]);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [thanks, setThanks] = useState(null);

  useEffect(() => {
    http.get("/truth-markers/page").then(({ data }) => setPage(data)).catch(() => {});
    http.get("/truth-markers/locations").then(({ data }) => setLocations(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await http.get(`/truth-markers/sponsor/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setThanks(data.sponsor);
        } else {
          setTimeout(poll, 2000);
        }
      } catch (e) { console.warn(e); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!page) return <Layout><div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">Loading…</div></Layout>;

  if (page.enabled === false) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center bg-[#050505] px-6 py-24">
          <div className="text-center max-w-md">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">{page.subtitle}</div>
            <h1 className="font-display text-4xl text-[#F4F1EA] mb-4">{page.title}</h1>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mb-5" />
            <p className="font-body text-[15px] text-[#B5B0A7]">Coming soon. We&rsquo;re putting the finishing touches on this part of the movement.</p>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="tm-hero">
        {page.hero_image ? (
          <div className="absolute inset-0">
            <img src={resolveImage(page.hero_image)} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-[#050505]" />
        )}
        <div className="relative max-w-3xl mx-auto px-6 py-24 sm:py-36 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-5">{page.subtitle}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F4F1EA] leading-[1.05] mb-7" data-testid="tm-title">
            {page.title}
          </h1>
          <p className="font-body text-[15px] leading-[1.8] text-[#B5B0A7] max-w-2xl mx-auto">{page.intro}</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <button onClick={() => setSponsorOpen(true)} data-testid="tm-sponsor-btn" className="btn-gold">
              {page.sponsor_cta}
            </button>
            <Link to={page.stickers_url || "/shop"} className="btn-outline flex items-center gap-2">
              <ShoppingBag size={14} /> {page.stickers_cta}
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT + HOW */}
      <section className="bg-[#0a0a0a] py-16 sm:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 grid sm:grid-cols-2 gap-10">
          <div data-testid="tm-what">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">{page.what_heading}</div>
            <p className="font-body text-[15px] leading-[1.8] text-[#B5B0A7]">{page.what_body}</p>
          </div>
          <div data-testid="tm-how">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">{page.how_heading}</div>
            <p className="font-body text-[15px] leading-[1.8] text-[#B5B0A7] whitespace-pre-line">{page.how_body}</p>
          </div>
        </div>
      </section>

      {/* SPONSORSHIP LEVELS */}
      <section className="bg-[#050505] py-16 sm:py-20 border-t border-white/5" data-testid="tm-sponsor-levels">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">{page.sponsor_heading}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA] mb-4">{page.sponsor_body}</h2>
            <div className="w-12 h-px bg-[#C9A055] mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(page.levels || []).map((lv) => (
              <button key={lv.slug || lv.label}
                      onClick={() => setSponsorOpen({ level: lv })}
                      data-testid={`tm-level-${lv.slug}`}
                      className="text-left bg-[#0a0a0a] border border-white/10 hover:border-[#C9A055] p-5 transition group">
                <div className="font-display text-2xl text-[#C9A055] group-hover:text-[#E0B872]">{lv.label}</div>
                <div className="font-body text-sm text-[#B5B0A7] mt-2 leading-relaxed">{lv.tagline}</div>
              </button>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => setSponsorOpen(true)} className="btn-outline">Custom amount</button>
          </div>
        </div>
      </section>

      {/* LOCATIONS GALLERY */}
      <section className="bg-[#0a0a0a] py-16 sm:py-20 border-t border-white/5" data-testid="tm-locations">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
            <div>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-2">Marker Map</div>
              <h2 className="font-display text-3xl text-[#F4F1EA]">Where the truth has been placed</h2>
            </div>
            <button onClick={() => setLocOpen(true)} data-testid="tm-submit-loc-btn" className="btn-outline flex items-center gap-2">
              <MapPin size={14} /> Submit a Marker Location
            </button>
          </div>
          {locations.length === 0 ? (
            <div className="text-center border border-dashed border-white/10 bg-[#050505] p-12">
              <MapPin size={28} className="mx-auto text-[#C9A055] opacity-60 mb-3" />
              <div className="font-display text-lg text-[#F4F1EA] mb-1">No markers placed yet.</div>
              <div className="font-body text-sm text-[#8a857d]">Be the first to put truth into the world.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((m) => (
                <div key={m.id} className="bg-[#050505] border border-white/10 overflow-hidden" data-testid={`tm-loc-${m.id}`}>
                  {m.photo_url && <img src={m.photo_url} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <div className="font-display text-[16px] text-[#F4F1EA] leading-snug">&ldquo;{m.truth}&rdquo;</div>
                    <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-2 flex items-center gap-1.5">
                      <MapPin size={11} /> {m.location_name}
                    </div>
                    {m.story && <p className="font-body text-[12px] italic text-[#8a857d] mt-2 line-clamp-3">{m.story}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GUIDELINES */}
      <section className="bg-[#050505] py-14 sm:py-20 border-t border-white/5" data-testid="tm-guidelines">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">Placement Guidelines</div>
          <div className="w-12 h-px bg-[#C9A055] mx-auto mb-6" />
          <p className="font-body text-[15px] leading-[1.85] text-[#B5B0A7]">{page.guidelines}</p>
        </div>
      </section>

      {sponsorOpen && (
        <SponsorModal
          page={page}
          preselect={sponsorOpen?.level || null}
          onClose={() => setSponsorOpen(false)}
        />
      )}
      {locOpen && (
        <LocationModal
          onClose={() => setLocOpen(false)}
          onSubmitted={() => { setLocOpen(false); toast.success("Submitted — pending admin review."); }}
        />
      )}
      {thanks && <ThanksModal sponsor={thanks} onClose={() => { setThanks(null); window.history.replaceState({}, "", "/truth-markers"); }} />}
    </Layout>
  );
}

function SponsorModal({ page, preselect, onClose }) {
  const [level, setLevel] = useState(preselect || null);
  const [amount, setAmount] = useState(preselect?.amount || 25);
  const [form, setForm] = useState({ name: "", business_name: "", email: "", city: "", message: "", anonymous: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required."); return; }
    if (!Number(amount) || Number(amount) < 1) { toast.error("Pick an amount."); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/truth-markers/sponsor", {
        ...form, amount: Number(amount), level_slug: level?.slug || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tm-sponsor-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Sponsor a Truth Marker</div>
            <div className="font-display text-xl text-[#F4F1EA]">Put more truth into the world</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {(page.levels || []).map((lv) => (
              <button type="button" key={lv.slug} onClick={() => { setLevel(lv); setAmount(lv.amount); }}
                      className={`p-3 border text-left transition ${level?.slug === lv.slug ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 hover:border-[#C9A055]"}`}>
                <div className="font-display text-base">{lv.label}</div>
                <div className="font-body text-[11px] opacity-80 leading-snug mt-0.5">{lv.tagline}</div>
              </button>
            ))}
          </div>
          <Field label="Custom amount ($)">
            <input type="number" min="1" step="1" value={amount} onChange={(e) => { setAmount(e.target.value); setLevel(null); }}
                   data-testid="tm-sponsor-amount" className="stc-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <input className="stc-input" placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tm-sponsor-name" />
            <input className="stc-input" placeholder="Business (optional)" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
          </div>
          <input className="stc-input" type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="tm-sponsor-email" />
          <input className="stc-input" placeholder="City (optional)" value={form.city} onChange={(e) => set("city", e.target.value)} />
          <textarea className="stc-input min-h-[80px]" placeholder="Optional message" value={form.message} onChange={(e) => set("message", e.target.value)} />
          <label className="flex items-center gap-2 font-body text-sm text-[#B5B0A7] cursor-pointer">
            <input type="checkbox" checked={form.anonymous} onChange={(e) => set("anonymous", e.target.checked)} className="accent-[#C9A055]" />
            Sponsor anonymously
          </label>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tm-sponsor-submit" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Lock size={14} /> {busy ? "Opening…" : `Sponsor $${Number(amount) || 0}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function LocationModal({ onClose, onSubmitted }) {
  const [form, setForm] = useState({
    truth: "", location_name: "", address: "", photo_url: "", story: "",
    submitted_by: "", email: "", permission: false, safety: false,
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo too large (10 MB max)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/truth-markers/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Convert to absolute URL so it loads even when referenced from CDN
      const absolute = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      set("photo_url", absolute);
      toast.success("Photo uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.truth.length < 4 || !form.location_name.trim()) { toast.error("Truth and location are required."); return; }
    if (!form.permission || !form.safety) { toast.error("Please confirm both placement consents."); return; }
    setBusy(true);
    // Sanitize: empty strings -> null so Pydantic EmailStr validation doesn't fail
    const clean = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v])
    );
    try {
      await http.post("/truth-markers/submit-location", clean);
      onSubmitted();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't submit.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tm-loc-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Truth Markers</div>
            <div className="font-display text-xl text-[#F4F1EA]">Submit a Marker Location</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="The truth on this marker">
            <textarea className="stc-input min-h-[70px]" maxLength={240} value={form.truth} onChange={(e) => set("truth", e.target.value)} data-testid="tm-loc-truth" />
          </Field>
          <Field label="Location name (e.g. North Park Bench)">
            <input className="stc-input" maxLength={120} value={form.location_name} onChange={(e) => set("location_name", e.target.value)} data-testid="tm-loc-name" />
          </Field>
          <Field label="Address or general area (optional)">
            <input className="stc-input" maxLength={240} value={form.address} onChange={(e) => set("address", e.target.value)} />
          </Field>
          <Field label="Photo (optional)">
            <div className="flex items-center gap-3">
              <label className="btn-outline cursor-pointer text-[11px] inline-flex items-center gap-1.5" data-testid="tm-loc-photo-label">
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="tm-loc-photo-input" />
                {uploading ? "Uploading…" : (form.photo_url ? "Replace photo" : "Choose photo")}
              </label>
              {form.photo_url && (
                <img src={form.photo_url} alt="" className="w-12 h-12 object-cover border border-white/10" />
              )}
            </div>
          </Field>
          <Field label="Story / reason for this location (optional)">
            <textarea className="stc-input min-h-[80px]" maxLength={2000} value={form.story} onChange={(e) => set("story", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <input className="stc-input" placeholder="Your name" value={form.submitted_by} onChange={(e) => set("submitted_by", e.target.value)} />
            <input className="stc-input" type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="border-t border-white/10 pt-3 space-y-2 font-body text-[13px] text-[#B5B0A7]">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.permission} onChange={(e) => set("permission", e.target.checked)} data-testid="tm-loc-permission" className="accent-[#C9A055] mt-1" />
              I have permission to place a Truth Marker at this location.
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.safety} onChange={(e) => set("safety", e.target.checked)} data-testid="tm-loc-safety" className="accent-[#C9A055] mt-1" />
              I understand the placement guidelines — no private property, government signs, monuments, or unauthorized public spaces.
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tm-loc-submit" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Sparkles size={14} /> {busy ? "Submitting…" : "Submit location"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ThanksModal({ sponsor, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tm-thanks-modal">
      <div className="bg-[#0a0a0a] border border-[#C9A055]/60 max-w-md w-full p-8 text-center">
        <Heart size={36} className="mx-auto text-[#C9A055] mb-4" />
        <div className="font-display text-2xl text-[#F4F1EA] mb-2">Thank you, {sponsor.name}.</div>
        <div className="font-body text-sm text-[#B5B0A7] mb-5">
          You sponsored <span className="text-[#C9A055]">{sponsor.level_label}</span> of Truth Markers — putting more reminders into the world.
        </div>
        <button onClick={onClose} className="btn-gold w-full">Continue</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
